import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { Button } from "../ui";
import { AuthCover, AuthHeading } from "./AuthCover";
import { verificationCode, resendVerificationCode } from "../../shared/_services/auth.services";
import { getLandingRouteForUser } from "../../shared/_helpers/landingRoute";
import { readCurrentUser } from "../../shared/_helpers/currentUser";
import { clearSession } from "../../shared/_helpers/session";
import { useAuth } from "../../context/AuthContext.jsx";
import { getStage } from "../../shared/_services/onboarding.services";
import {
  LOAD_FAILED,
  TRY_AGAIN,
  VERIFY_CONTINUE,
  VERIFY_COOLDOWN,
  VERIFY_EXPIRY_LEAD,
  VERIFY_GO_BACK,
  VERIFY_H1,
  VERIFY_HELPER,
  VERIFY_RESEND,
  VERIFY_RESEND_TOAST,
  VERIFY_RESENT,
  VERIFY_SEND_FAILED,
  VERIFY_SENT_TO,
  VERIFY_SIDE_NEXT,
  VERIFY_SIDE_TITLE,
} from "../onboarding/onboardingCopy";
import {
  clearChallengeToken,
  getChallengePurpose,
  getChallengeToken,
  setChallengeToken,
  setResetToken,
} from "../../shared/_helpers/otpChallenge";

// The server refuses a resend inside 60 s of the last send (SP3), so the
// screen counts the same 60 s down before offering one.
export const RESEND_COOLDOWN_SECONDS = 60;

const VerifyCode = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { refetch: refetchAuth } = useAuth();
  const [, setTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [timerActive, setTimerActive] = useState(false);
  // "Resend code" reads "Code sent again" until the cooldown after a resend ends.
  const [resent, setResent] = useState(false);
  // R173: a client's stage read failed after their code was already redeemed
  // — Try again only re-reads the stage, never the code.
  const [stageLoadFailed, setStageLoadFailed] = useState(false);
  const location = useLocation();
  const stateFrom = location?.state?.from;
  const storedFrom = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('verifyCodeFrom') : null;
  const fromPage = stateFrom || storedFrom || null;

  useEffect(() => {
    if (stateFrom) {
      try {
        sessionStorage.setItem('verifyCodeFrom', stateFrom);
        console.log('[Verification] Persisted verifyCodeFrom to sessionStorage:', stateFrom);
      } catch (_) {}
    }
  }, [stateFrom]);

  // R175: a client whose stored session already sits at
  // signup_pending_verification but carries no challenge token — a reload
  // after the token expired, verify-jwttoken restoring a session in a fresh
  // tab, or a backfilled unverified client — cannot verify here: otp-verify
  // and resend-otp both need that token. The signup → verify handoff sets
  // the token before navigating here, so that path is untouched; anyone else
  // is sent back to sign in, which re-verifies the phone and advances the
  // stage server-side.
  useEffect(() => {
    const stored = readCurrentUser();
    const stuckWithNoToken =
      stored?.user?.userType === "client" &&
      stored?.user?.onboardingStage === "signup_pending_verification" &&
      !getChallengeToken();
    if (stuckWithNoToken) {
      clearSession();
      enqueueSnackbar(
        "This verification link has expired. Please sign in again to get a new code.",
        { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } },
      );
      navigate("/login");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only check

  useEffect(() => {
    const path = getBackPath();
    console.log('[Verification] location.state:', location?.state, '| fromPage:', fromPage, '| Back path:', path);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- log once on mount

  const getBackPath = () => {
    if (fromPage === 'login' || fromPage === 'login-advisor' || fromPage === 'login-admin') return '/login';
    if (fromPage === 'signup') return '/signup';
    if (fromPage === 'forgotpassword') return '/forgot-password';
    return '/login';
  };

  // The three post-login markers land on the same per-role route the role
  // guards redirect to; signup and the rest keep today's fixed landings. A
  // null routeFor on a successful stage read (trial_start and later) falls
  // through to this — shared with the R173 retry, which re-reads the stage
  // after a rejected read rather than repeating this switch.
  const landLegacy = (stageKey) => {
    const currentUser = readCurrentUser();
    switch (fromPage) {
      case "login":
      case "login-advisor":
      case "login-admin":
        navigate(getLandingRouteForUser(currentUser?.user));
        break;
      case "signup":
        // R208: never a hard-coded /welcome (that is trial_start's route only).
        navigate(
          getLandingRouteForUser({
            ...currentUser?.user,
            onboardingStage: stageKey,
          }),
        );
        break;
      default:
        // R208: no hard-coded /welcome here either.
        navigate(
          getLandingRouteForUser({
            ...currentUser?.user,
            userType: currentUser?.user?.userType || "client",
            onboardingStage: stageKey,
          }),
        );
    }
  };

  // R173: Try again after a rejected stage read. The code is already
  // redeemed and the session refetched, so this only re-reads the stage and
  // navigates via the same routeFor.web / legacy-when-null logic as the
  // original submit.
  const retryStageRead = async () => {
    try {
      const stage = await getStage();
      const web = stage.data?.routeFor?.web;
      setStageLoadFailed(false);
      if (web) {
        navigate(web, { state: { resumed: fromPage !== "signup" } });
        return;
      }
      landLegacy(stage.data?.stage);
    } catch (_) {
      // Stays failed; Try again remains in place.
    }
  };

  const initialValues = {
    otp1: "",
    otp2: "",
    otp3: "",
    otp4: "",
    otp5: "",
    otp6: "",
  };

  const validationSchema = Yup.object().shape({
    otp1: Yup.string().required(),
    otp2: Yup.string().required(),
    otp3: Yup.string().required(),
    otp4: Yup.string().required(),
    otp5: Yup.string().required(),
    otp6: Yup.string().required(),
  }).test('allFilled', 'All verification code fields are required', function (value) {
    return Object.values(value).every(otp => otp.trim() !== '');
  });

  const getOtpSlotIndex = (name) => {
    const m = /^otp(\d+)$/.exec(name);
    return m ? parseInt(m[1], 10) : NaN;
  };

  const handleInputChange = (e, setFieldValue) => {
    const { maxLength, value, name } = e.target;
    const slot = getOtpSlotIndex(name);

    if (value.length >= maxLength && !Number.isNaN(slot) && slot < 6) {
      const nextField = document.querySelector(`input[name=otp${slot + 1}]`);
      if (nextField !== null) {
        nextField.focus();
      }
    }
    setFieldValue(name, value);
  };

  const handleKeyDown = (e, setFieldValue, values) => {
    const { name, value } = e.target;
    const slot = getOtpSlotIndex(name);

    if (e.key === 'Backspace' && !value && !Number.isNaN(slot) && slot > 1) {
      const prevField = document.querySelector(`input[name=otp${slot - 1}]`);
      if (prevField !== null) {
        prevField.focus();
        setFieldValue(`otp${slot - 1}`, '');
      }
    }
  };

  const handlePaste = (e, setFieldValue, validateForm) => {
    const paste = e.clipboardData.getData('text');
    if (paste.length === 6) {
      paste.split('').forEach((char, index) => {
        setFieldValue(`otp${index + 1}`, char);
      });
      document.querySelector(`input[name=otp6]`).focus();

      setTimeout(() => {
        validateForm();
      }, 0);
    }
    e.preventDefault();
  };


  const handleSubmit = async (values) => {
    const otp = `${values.otp1}${values.otp2}${values.otp3}${values.otp4}${values.otp5}${values.otp6}`;

    // Backend SEC-114: the code is redeemable only by the request that asked
    // for it. `email` alone was the old credential and is now ignored by the
    // server — the target account is the challenge token's subject — so a
    // missing challenge is a dead end, not something to retry with an email.
    const challengeToken = getChallengeToken();
    // The forgot-password flow is the one entrance whose next screen needs a
    // credential of its own: POST /users/reset-password requires the single-use
    // reset token (backend SEC-084), and /otp-verify only mints it when the code
    // is redeemed with this purpose. No other entrance may send it — a reset
    // token minted during an ordinary login would be a live password-change
    // credential nobody asked for, and /otp-verify returns it INSTEAD of the
    // session, so that login would also silently fail.
    //
    // Read from the challenge, never from `fromPage`. The `verifyCodeFrom`
    // breadcrumb outlives its flow: Login stores the challenge before it decides
    // whether to navigate, and ClientDashboard sends an unverified user here with
    // neither navigation state nor a breadcrumb write — so a live login challenge
    // can meet a stale `forgotpassword` breadcrumb.
    const challengePurpose = getChallengePurpose();
    const isPasswordResetFlow = challengePurpose === "reset_password";

    if (!challengeToken) {
      enqueueSnackbar(
        "This verification link has expired. Please sign in again to get a new code.",
        { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
      );
      navigate(getBackPath());
      return;
    }

    try {
      let data = await verificationCode({
        otp,
        challengeToken,
        ...(isPasswordResetFlow ? { purpose: "reset_password" } : {}),
      });

      if (data.status === 200) {
        // Defaulted, not passed straight through: notistack throws on an
        // undefined message, and this call sits in front of the session write —
        // a 200 that happened to carry no `message` would throw into the catch
        // below and silently lose the token /otp-verify just minted.
        enqueueSnackbar(data?.data?.message || "Verification successful.", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });

        // Redeemed — it is single-use, so it must not survive into a later flow.
        clearChallengeToken();

        // readCurrentUser, not a raw JSON.parse: localStorage is user-editable
        // and a corrupt value would throw into the catch below, losing the
        // session token this response just carried (SEC-028's reasoning, applied
        // to a write rather than a read).
        const currentUser = readCurrentUser() || {};

        // SEC-114 moved the session here: login's response no longer carries a
        // token, and this is the first point at which both factors have been
        // presented. `data.data.data` is present only for a login challenge
        // (`pwd: true`); the signup and forgot-password entrances verify a
        // phone or mint a reset token and yield no session.
        const session = data?.data?.data;
        if (session?.token) {
          currentUser.user = session;
          currentUser.token = session.token;
          currentUser.refreshToken = session.refreshToken;
        }

        // SEC-084: the proof of the OTP step that /reset-password now demands.
        setResetToken(data?.data?.resetToken);

        if (currentUser.user) {
          currentUser.user.isPhoneVerified = true;
          localStorage.setItem("currentUser", JSON.stringify(currentUser));
          // SEC-066: AuthContext already ran its one verification pass on
          // app load, before this screen minted a session, and settled on
          // "unauthenticated". Without refetching here, the next
          // ProtectedRoutes-guarded screen would trust that stale status and
          // bounce straight back to "/" despite the token that now exists.
          // Signup minted its session on /users/create, also after that pass,
          // and SP3 lands a new client on a guarded page, so it refetches too.
          if (session?.token || fromPage === "signup") {
            refetchAuth();
          }
        }

        // Decided by the credential, for the same reason the request is: only a
        // challenge that actually carried the reset purpose may land the user on
        // the password-reset screen. A stale `forgotpassword` breadcrumb falls
        // through to the switch below, which no longer has a case for it.
        if (isPasswordResetFlow) {
          navigate("/reset-password", { state: { from: "verification" } });
          return;
        }

        // Pooled inbox SP3: a client lands on the page their onboarding stage
        // names (the server advanced them past verification just now). A
        // null routeFor on a SUCCESSFUL read — trial_start and later — still
        // falls through to the legacy switch below. A REJECTED read (R173)
        // must not: the code is already redeemed, so falling through would
        // send a new client around the documents pages into the legacy
        // questionnaire. Show the failure in place instead; Try again
        // (retryStageRead) only re-reads the stage.
        let stageKey;
        if (currentUser?.user?.userType === "client") {
          try {
            const stage = await getStage();
            stageKey = stage.data?.stage;
            const web = stage.data?.routeFor?.web;
            if (web) {
              navigate(web, { state: { resumed: fromPage !== "signup" } });
              return;
            }
          } catch (_) {
            setStageLoadFailed(true);
            return;
          }
        }

        landLegacy(stageKey);
      } else if (data.response?.status === 400) {
        enqueueSnackbar(data?.response?.data?.message, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      } else if (data.response?.status === 401) {
        // The challenge expired (five minutes) or the account was blocked
        // between the password step and this one. Either way it cannot be
        // recovered from this screen, and a stale token must not be replayed.
        clearChallengeToken();
        enqueueSnackbar(
          data?.response?.data?.message ||
            "This verification session has expired. Please sign in again.",
          { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
        );
        navigate(getBackPath());
      }
    } catch (error) {
      console.error("Error verifying verification code:", error);
    }
  };


  useEffect(() => {
    let interval = null;
    if (timerActive) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setTimerActive(false);
            setResent(false);
            clearInterval(interval);
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive]);

  const handleResend = async () => {
    // Backend SEC-114: a resend belongs to the flow that started it, so it
    // presents the outstanding challenge rather than a bare email — an email
    // alone no longer buys SMS sends to someone else's phone.
    const challengeToken = getChallengeToken();
    if (!challengeToken) {
      enqueueSnackbar("This verification link has expired. Please sign in again to get a new code.", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate(getBackPath());
      return;
    }

    const response = await resendVerificationCode({ challengeToken });
    if (response.status === 200) {
      // The re-issued challenge keeps the original flow anchor and replaces the
      // one just spent; the superseded code no longer verifies (INT-016), so
      // keeping the old token would strand the new code.
      setChallengeToken(response?.data?.challengeToken, {
        purpose: getChallengePurpose(),
      });
      enqueueSnackbar(VERIFY_RESEND_TOAST, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setResent(true);
      setTimer(RESEND_COOLDOWN_SECONDS);
      setTimerActive(true);
    } else if (response?.response?.status === 429) {
      // Inside the server's cooldown: its message says how long is left.
      enqueueSnackbar(response.response.data?.message || VERIFY_COOLDOWN, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setTimer(response.response.data?.retryAfterSeconds || RESEND_COOLDOWN_SECONDS);
      setTimerActive(true);
    } else if (response?.response?.status === 401) {
      // The 15-minute flow window has passed, or the challenge expired.
      clearChallengeToken();
      enqueueSnackbar(response?.response?.data?.message || "This verification session has expired. Please sign in again.", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate(getBackPath());
    } else {
      enqueueSnackbar(response?.response?.data?.error?.message || VERIFY_SEND_FAILED, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  };

  // Start timer initial load
  useEffect(() => {
    setTimerActive(true);
    return () => {
      setTimerActive(false);
    };
  }, []);


  const mobile = readCurrentUser()?.user?.mobile;

  const codeFields = ({ setFieldValue, values, validateForm }) => {
    const complete = Object.values(values).every((v) => String(v).trim() !== "");
    return (
      <>
        <AuthHeading title={VERIFY_H1} lede={mobile ? VERIFY_SENT_TO(mobile) : null} />
        <div data-slot="otp-box" className="tw-mb-3 tw-grid tw-max-w-sm tw-grid-cols-6 tw-gap-2">
          {['otp1', 'otp2', 'otp3', 'otp4', 'otp5', 'otp6'].map((otp, i) => (
            <Field
              key={otp}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              name={otp}
              maxLength="1"
              aria-label={`${i + 1}`}
              className="tw-aspect-square tw-min-h-12 tw-w-full tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-surface tw-text-center tw-text-xl tw-font-bold tw-text-text focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-focus-ring"
              onChange={(e) => handleInputChange(e, setFieldValue)}
              onPaste={(e) => handlePaste(e, setFieldValue, validateForm)}
              onKeyDown={(e) => handleKeyDown(e, setFieldValue)}
            />
          ))}
        </div>
        {!complete && <p className="tw-mb-2 tw-text-xs tw-text-text-muted">{VERIFY_HELPER}</p>}
        <p className="tw-mb-2 tw-text-xs tw-text-text-muted">
          {VERIFY_EXPIRY_LEAD}
          <Link className="tw-font-bold tw-text-link" to={getBackPath()}>
            {VERIFY_GO_BACK}
          </Link>
          .
        </p>
      </>
    );
  };

  // R173: in place of the code fields and actions once the code is redeemed
  // but the stage read that follows it failed — the same LOAD_FAILED line
  // and TRY_AGAIN button the other onboarding pages use.
  const stageFailure = (
    <div role="alert" className="tw-text-sm">
      <span>{LOAD_FAILED}</span>{" "}
      <Button type="button" variant="text" onClick={retryStageRead}>
        {TRY_AGAIN}
      </Button>
    </div>
  );

  const actions = ({ isSubmitting }) => (
    <div className="tw-mt-6 tw-grid tw-gap-3">
      {timerActive && <p className="tw-text-xs tw-text-text-muted">{VERIFY_COOLDOWN}</p>}
      <Button type="submit" className="tw-w-full" disabled={isSubmitting}>
        {VERIFY_CONTINUE}
      </Button>
      <Button
        type="button"
        variant="quiet"
        className="tw-w-full"
        onClick={handleResend}
        disabled={timerActive}
      >
        {resent && timerActive ? VERIFY_RESENT : VERIFY_RESEND}
      </Button>
    </div>
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form>
          {fromPage === "signup" ? (
            // The signup flow: the cover with the wordmark only, the code, then
            // the actions and the "Next:" line in the aside.
            <AuthCover>
              {stageLoadFailed ? stageFailure : codeFields(formik)}
              <aside aria-label={VERIFY_SIDE_TITLE}>
                <h2 className="tw-mb-3 tw-text-lg tw-font-bold tw-text-text">{VERIFY_SIDE_TITLE}</h2>
                {!stageLoadFailed && actions(formik)}
                <p className="tw-mt-3 tw-text-xs tw-text-text-muted">{VERIFY_SIDE_NEXT}</p>
              </aside>
            </AuthCover>
          ) : (
            // Sign-in and password reset: the same cover, with its marketing lines;
            // only the copy in the box is SP3's.
            <AuthCover
              art={
                <div className="tw-grid tw-gap-4">
                  <h2 className="tw-m-0 tw-max-w-sm tw-text-3xl tw-font-semibold tw-text-text">
                    Verify Your Account
                  </h2>
                  <p className="tw-m-0 tw-max-w-md tw-text-text">
                    We’ve sent a verification code to your registered phone number & email. Please enter the code below to confirm your identity and continue to your financial dashboard.
                  </p>
                  <p className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-text max-rail:tw-hidden">
                    PROFESSIONAL FINANCIAL ADVISORY</p>
                </div>
              }
            >
              {stageLoadFailed ? (
                stageFailure
              ) : (
                <>
                  {codeFields(formik)}
                  {actions(formik)}
                </>
              )}
            </AuthCover>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default VerifyCode;
